import type {
  ChannelProvider,
  DeliveryTarget,
  ResolvedDispatch,
  ResolveError,
  ResolverAgent,
  ResolverPerson,
} from './types.js';

/** Resolve a `DeliveryTarget` to a concrete dispatch (channel id or
 *  slack_user_id/chat_id) at *fire time*. ENG-4423 §5.
 *
 *  Applies the follow_reports_to indirection, the preferred-medium
 *  fallback, and enforces the invariants that should produce a hard
 *  failure rather than silent misdelivery.
 *
 *  `people` is a lookup map indexed by `person_id`. The caller is expected
 *  to have pre-fetched the people the agent might DM (reports_to person +
 *  org dm people). Missing keys produce `DM_TARGET_PERSON_NOT_FOUND`. */
export function resolveDmTarget(
  target: DeliveryTarget,
  agent: ResolverAgent,
  people: ReadonlyMap<string, ResolverPerson>,
): ResolvedDispatch | ResolveError {
  // Channel targets resolve trivially — no lookups, no fallback.
  if (target.kind === 'channel') {
    if (target.provider === 'slack') {
      return {
        ok: true,
        kind: 'channel',
        provider: 'slack',
        channel_id: target.channel_id ?? '',
        // ENG-6038: carry the originating-thread coordinate through to
        // dispatch. Absent → top-level post (pre-ENG-6038 behaviour).
        ...(target.thread_ts ? { thread_ts: target.thread_ts } : {}),
      };
    }
    return {
      ok: true,
      kind: 'channel',
      provider: 'telegram',
      chat_id: target.chat_id ?? '',
    };
  }

  // DM targets: resolve the effective person and pick a medium.
  const effectivePersonId = resolveEffectivePersonId(target, agent);
  if ('ok' in effectivePersonId) return effectivePersonId;

  const person = people.get(effectivePersonId.person_id);
  if (!person) {
    return {
      ok: false,
      code: 'DM_TARGET_PERSON_NOT_FOUND',
      detail: `person ${effectivePersonId.person_id} not present in resolver people map`,
    };
  }

  // Deterministic fallback order. Freezing the order here keeps behaviour
  // stable across releases — see ENG-4423 §5 step 4c.
  const FALLBACK_ORDER: ChannelProvider[] = ['slack', 'telegram'];

  const preferredMedium = target.medium === 'auto' ? null : target.medium;

  const chosenMedium = preferredMedium
    ? (agent.dm_capable_mediums.includes(preferredMedium) &&
        personHasMedium(person, preferredMedium)
        ? preferredMedium
        : null)
    : FALLBACK_ORDER.find(
        (m) =>
          agent.dm_capable_mediums.includes(m) && personHasMedium(person, m),
      ) ?? null;

  if (!chosenMedium) {
    return {
      ok: false,
      code: 'DM_TARGET_NO_REACHABLE_MEDIUM',
      detail: `agent and person ${person.person_id} share no DM-capable medium`,
    };
  }

  if (chosenMedium === 'slack') {
    return {
      ok: true,
      kind: 'dm',
      medium: 'slack',
      slack_user_id: person.slack_user_id!,
      recipient_person_id: person.person_id,
    };
  }
  return {
    ok: true,
    kind: 'dm',
    medium: 'telegram',
    telegram_chat_id: person.telegram_chat_id!,
    recipient_person_id: person.person_id,
  };
}

function resolveEffectivePersonId(
  target: Extract<DeliveryTarget, { kind: 'dm' }>,
  agent: ResolverAgent,
): { person_id: string } | ResolveError {
  if (target.follow_reports_to) {
    if (agent.reports_to_type !== 'person' || !agent.reports_to_person_id) {
      return {
        ok: false,
        code: 'DM_FOLLOW_TARGET_NOT_PERSON',
        detail:
          'follow_reports_to=true but the agent has no person-typed reports_to at dispatch time',
      };
    }
    return { person_id: agent.reports_to_person_id };
  }
  return { person_id: target.person_id };
}

function personHasMedium(
  person: ResolverPerson,
  medium: ChannelProvider,
): boolean {
  if (medium === 'slack') return Boolean(person.slack_user_id);
  return Boolean(person.telegram_chat_id);
}

/** Narrow helper: true when the resolve result is a resolver error. */
export function isResolveError(
  v: ResolvedDispatch | ResolveError,
): v is ResolveError {
  return 'ok' in v && v.ok === false;
}
